import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import { get } from 'lodash-es';
import { memo, useMemo } from 'react';
import { useCallbackRef } from '../../../../../hooks';
import { SearchSelect } from '../../../../Form/SearchSelect/SearchSelect';
import type { DropdownDrawerOption, InputsAndDropdownsDrawerDropdownOption } from '../types';
import { drawerContentMemoComparison, type DrawerContentProps } from './utils';

const DropdownContentInner = <TDrawerRecord extends EntityAdminRecord>({
  drawerOption,
  form,
  originalEntity,
  isEditing,
  onFormUpdate,
  inheritedValue,
}: DrawerContentProps<DropdownDrawerOption<TDrawerRecord>, TDrawerRecord>) => {
  const dropdownOptions = useMemo(() => {
    return drawerOption.getDropdownOptions(form, isEditing);
  }, [drawerOption, form, isEditing]);
  const dropdownOptionsByValueMap = useMemo(() => {
    return new Map(dropdownOptions.map(option => [option.value, option]));
  }, [dropdownOptions]);
  const placeholder = useMemo(() => {
    return get(form, drawerOption.field)
      ? get(form, drawerOption.field)
      : inheritedValue != null
        ? (dropdownOptionsByValueMap.get(inheritedValue)?.label ?? inheritedValue)
        : drawerOption.placeholder;
  }, [drawerOption, form, inheritedValue, dropdownOptionsByValueMap]);

  const selection = useMemo(() => {
    return dropdownOptionsByValueMap.get(get(form, drawerOption.field));
  }, [dropdownOptionsByValueMap, form, drawerOption.field]);
  const disabled = useMemo(() => {
    return (drawerOption.disabledWhenEditing && isEditing) || drawerOption.getIsDisabled?.(form, originalEntity);
  }, [drawerOption, form, originalEntity, isEditing]);
  const onChange = useCallbackRef((newValue: InputsAndDropdownsDrawerDropdownOption<string | boolean> | undefined) => {
    const value = newValue?.value ?? '';
    onFormUpdate(drawerOption.field, value);
    drawerOption.onChange?.(value, onFormUpdate, form);
  });

  return (
    <SearchSelect
      selection={selection}
      options={dropdownOptions}
      getLabel={option => option.label}
      getDescription={option => option.description ?? ''}
      onChange={onChange}
      showClear={drawerOption.showClear ?? true}
      disabled={disabled}
      placeholder={placeholder}
      data-testid={`entity-admin-drawer-${String(drawerOption.field)}`}
      {...drawerOption.searchSelectProps}
    />
  );
};

export const DropdownContent = memo(DropdownContentInner, drawerContentMemoComparison) as <
  TDrawerRecord extends EntityAdminRecord,
>(
  props: DrawerContentProps<DropdownDrawerOption<TDrawerRecord>, TDrawerRecord>
) => JSX.Element;
